import type { IconProps } from '@phosphor-icons/react';
import type { FC } from 'react';

import { HeartIcon, HeartStraightIcon } from '@phosphor-icons/react';
import styled, { css, useTheme } from 'styled-components';

import { getWishlistAriaLabel } from './utilities/getWishlistAriaLabel';
import { FesLink } from '../../core/link/FesLink';
import { Typography } from '../../core/typography/Typography';
import { HeaderButton } from '../HeaderStyle';
import { useWishlistContext } from '~/utilities/context/dynamic/WishlistContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

const S = {
  WishListButton: styled(FesLink)`
    ${HeaderButton}
  `,

  Amount: styled(Typography)`
    ${({ theme }) => css`
      color: ${theme.colors.content.primary};

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          font-weight: 400;
        `,
      })}
    `}
  `,
};

const HeartStraightIconBold = ({ size, color }: IconProps) => (
  <HeartStraightIcon weight="bold" size={size} color={color} />
);

const HeartIconMap: Record<SiteType, FC<IconProps>> = {
  [SiteType.Aaf]: HeartStraightIcon,
  [SiteType.Employeeshop]: HeartStraightIconBold,
  [SiteType.Gstar]: HeartStraightIconBold,
  [SiteType.Outlet]: HeartStraightIconBold,
  [SiteType.Wahts]: HeartIcon,
};

export const WishListButton: FC = () => {
  const { wishlist, content } = useWishlistContext();
  const theme = useTheme();
  const { siteType } = useAppContext();

  const wishlistAmount = wishlist?.products?.length ?? 0;
  const { wishlistAriaLabel } = getWishlistAriaLabel({ content, wishlistAmount });

  const HeartIcon = HeartIconMap[siteType];

  return (
    <S.WishListButton
      data-testid="wishlist-icon"
      href="/wishlist"
      aria-label={wishlistAriaLabel}
      data-cs-capture=""
    >
      <HeartIcon size={theme.components.icon.topNavigation.size} />
      {wishlistAmount > 0 && (
        <S.Amount
          aria-hidden="true"
          component="span"
          variant="title"
          testId="wishlist-count"
          data-cs-capture=""
        >
          {wishlistAmount}
        </S.Amount>
      )}
    </S.WishListButton>
  );
};
